/**
 * Lists as tables (M31): sorting by a column header, a search box, choosing
 * columns, and paging, on TanStack Table v9 (headless; the markup here is a
 * real <table> with a caption and sortable headers announced to screen
 * readers). Lists the server pages itself render their page as given, with
 * a `Pager` below.
 */
import { type ColumnDef, type ReactTable, type RowData, useTable } from "@tanstack/react-table";
import * as Popover from "@radix-ui/react-popover";
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Columns3,
  Search,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import { Button } from "./button";
import { type DataTableFeatures, dataTableFeatures } from "./data-table-columns";

const EMPTY: never[] = [];

export function DataTable<T extends RowData>({
  data,
  columns,
  caption,
  search = false,
  searchLabel,
  columnChooser = false,
  pageSize = 10,
  paginate = true,
  sortable = true,
  toolbar,
  empty,
  getRowId,
}: {
  data: T[] | undefined;
  // The column helper's definitions, whatever their value types.
  columns: unknown[];
  /** What the table lists, for screen readers. */
  caption: string;
  /** A search box filtering every searchable column. */
  search?: boolean;
  searchLabel?: string;
  /** A menu to show or hide columns. */
  columnChooser?: boolean;
  pageSize?: number;
  /** Page the rows here; off for lists the server pages. */
  paginate?: boolean;
  /** Sort by clicking a header; off for lists the server pages. */
  sortable?: boolean;
  /** Filters or actions beside the search box. */
  toolbar?: ReactNode;
  /** Shown when there are no rows at all (not when a search finds none). */
  empty?: ReactNode;
  getRowId?: (row: T) => string;
}) {
  const { t } = useTranslation();
  const [globalFilter, setGlobalFilter] = useState("");
  const table = useTable({
    features: dataTableFeatures,
    columns: columns as ColumnDef<DataTableFeatures, T>[],
    data: data ?? (EMPTY as T[]),
    getRowId: getRowId ? (row: T) => getRowId(row) : undefined,
    enableSorting: sortable,
    globalFilterFn: "includesString",
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    initialState: { pagination: { pageIndex: 0, pageSize: paginate ? pageSize : 100_000 } },
  });

  const rows = table.getRowModel().rows;
  const total = table.getPrePaginatedRowModel().rows.length;
  const showPager = paginate && total > pageSize;
  const { pageIndex } = table.state.pagination;

  if ((data ?? EMPTY).length === 0 && empty) {
    return <>{empty}</>;
  }

  return (
    <div className="flex flex-col gap-3">
      {search || columnChooser || toolbar ? (
        <div className="flex flex-wrap items-center gap-2">
          {search ? (
            <label className="relative flex min-w-56 flex-1 items-center sm:max-w-xs">
              <span className="sr-only">{searchLabel ?? t("ui.search")}</span>
              <Search aria-hidden="true" className="pointer-events-none absolute left-2.5 size-4 text-muted-foreground" />
              <input
                type="search"
                value={globalFilter}
                onChange={(event) => { setGlobalFilter(event.target.value); table.setPageIndex(0); }}
                placeholder={searchLabel ?? t("ui.search")}
                className="min-h-9 w-full rounded-md border border-input bg-background pl-8 pr-3 text-sm"
              />
            </label>
          ) : null}
          {toolbar}
          {columnChooser ? <ColumnChooser table={table} /> : null}
        </div>
      ) : null}
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">{caption}</caption>
          <thead className="bg-muted/60">
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id} className="border-b border-border">
                {group.headers.map((header) => {
                  const sorted = header.column.getIsSorted();
                  const canSort = header.column.getCanSort();
                  const meta = header.column.columnDef.meta;
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      aria-sort={sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : undefined}
                      className={cn("h-10 whitespace-nowrap px-3 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground", meta?.className)}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="-mx-1 inline-flex items-center gap-1 rounded px-1 py-1 uppercase hover:text-foreground"
                        >
                          <table.FlexRender header={header} />
                          {sorted === "asc" ? <ArrowUp aria-hidden="true" className="size-3.5" /> : sorted === "desc" ? <ArrowDown aria-hidden="true" className="size-3.5" /> : <ChevronsUpDown aria-hidden="true" className="size-3.5 opacity-50" />}
                        </button>
                      ) : (
                        <table.FlexRender header={header} />
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={table.getVisibleLeafColumns().length} className="px-3 py-10 text-center text-muted-foreground">
                  {t("ui.noMatches")}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="border-b border-border last:border-0 hover:bg-muted/40">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className={cn("px-3 py-2.5 align-middle", cell.column.columnDef.meta?.className)}>
                      <table.FlexRender cell={cell} />
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {showPager ? (
        <Pager
          page={pageIndex + 1}
          pageCount={table.getPageCount()}
          total={total}
          onPage={(page) => { table.setPageIndex(page - 1); }}
        />
      ) : null}
    </div>
  );
} // prettier-ignore

function ColumnChooser<T extends RowData>({ table }: { table: ReactTable<DataTableFeatures, T> }) {
  const { t } = useTranslation();
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <Button variant="secondary" size="md" className="ml-auto">
          <Columns3 aria-hidden="true" className="size-4" />
          {t("ui.columns")}
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={6} className="z-50 flex min-w-48 flex-col gap-1 rounded-md border border-border bg-popover p-2 shadow-lg">
          {table.getAllLeafColumns().filter((column) => column.getCanHide()).map((column) => {
            const label = column.columnDef.meta?.label ?? (typeof column.columnDef.header === "string" ? column.columnDef.header : column.id);
            return (
              <label key={column.id} className="flex min-h-8 cursor-pointer items-center gap-2 rounded px-2 text-sm hover:bg-muted">
                <input type="checkbox" className="size-4" checked={column.getIsVisible()} onChange={column.getToggleVisibilityHandler()} />
                {label}
              </label>
            );
          })}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
} // prettier-ignore

/** Page controls: "Page 2 of 5 · 48 in all", previous and next. */
export function Pager({
  page,
  pageCount,
  total,
  onPage,
}: {
  page: number;
  pageCount: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <nav aria-label={t("ui.pages")} className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
      <p>{t("ui.pageOf", { page, pageCount: Math.max(pageCount, 1), total })}</p>
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => { onPage(page - 1); }}>
          <ChevronLeft aria-hidden="true" className="size-4" />
          {t("ui.previous")}
        </Button>
        <Button variant="secondary" size="sm" disabled={page >= pageCount} onClick={() => { onPage(page + 1); }}>
          {t("ui.next")}
          <ChevronRight aria-hidden="true" className="size-4" />
        </Button>
      </div>
    </nav>
  );
} // prettier-ignore
